import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Printer,
  Share2,
  Sparkles,
  Lock,
  Plus,
  ExternalLink,
  Crown,
  ArrowRight,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, printReportUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const STATUS_META = {
  locked: { label: "Locked", className: "bg-slate-100 text-ink-600" },
  unlocked_one_off: { label: "Unlocked · one-off", className: "bg-brand-blue-light text-brand-blue" },
  unlocked_subscription: { label: "Unlimited", className: "bg-brand-green-light text-brand-green-hover" },
  unlocked: { label: "Unlocked", className: "bg-brand-blue-light text-brand-blue" },
  sample: { label: "Sample", className: "bg-slate-100 text-ink-600" },
};

async function copyLink(reportId) {
  const url = `${window.location.origin}/r/${reportId}`;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
      return;
    }
  } catch (_) { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = url;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    toast.success("Link copied");
  } catch (_) {
    toast.error("Could not copy link");
  }
}

export default function Dashboard() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [rp, sub] = await Promise.all([
          api.get("/dashboard/reports"),
          api.get("/dashboard/subscription"),
        ]);
        setReports(rp.data.reports || []);
        setSubscription(sub.data);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load your dashboard.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function openPortal() {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal");
      window.location.assign(data.portal_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal.");
      setPortalLoading(false);
    }
  }

  return (
    <>
      <Header variant="app" />
      <section className="max-w-5xl mx-auto px-6 py-10 md:py-14">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-blue">Dashboard</span>
            <h1
              data-testid="dashboard-headline"
              className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-ink-900"
            >
              My workups
            </h1>
            <p className="mt-2 text-ink-600">
              Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
            </p>
          </div>
          <Button
            data-testid="dashboard-new-btn"
            onClick={() => navigate("/start")}
            className="rounded-full bg-brand-blue hover:bg-brand-blue-hover text-white"
          >
            <Plus className="h-4 w-4 mr-1.5" strokeWidth={2.4} />
            New workup
          </Button>
        </div>

        {/* Subscription card */}
        <div
          data-testid="dashboard-subscription-card"
          className={`mt-8 rounded-2xl border p-6 flex flex-wrap items-center justify-between gap-4 ${
            subscription?.subscription_active
              ? "bg-ink-900 text-white border-ink-900"
              : "bg-white border-slate-100"
          }`}
        >
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${
                subscription?.subscription_active
                  ? "bg-brand-green text-white"
                  : "bg-slate-100 text-ink-600"
              }`}
            >
              <Crown className="h-5 w-5" strokeWidth={2.2} />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest opacity-80">
                Subscription
              </p>
              <p
                data-testid="dashboard-subscription-status"
                className="font-heading text-lg font-semibold"
              >
                {subscription?.subscription_active
                  ? "Active — Unlimited $19/month"
                  : "None"}
              </p>
            </div>
          </div>
          {subscription?.subscription_active ? (
            <Button
              onClick={openPortal}
              disabled={portalLoading}
              data-testid="dashboard-manage-billing"
              className="rounded-full bg-white text-ink-900 hover:bg-slate-100"
            >
              {portalLoading ? "Opening…" : "Manage billing"}
              <ExternalLink className="h-4 w-4 ml-1.5" strokeWidth={2.4} />
            </Button>
          ) : (
            <Link
              to="/start"
              data-testid="dashboard-upgrade-cta"
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-blue text-white px-5 py-2.5 text-sm font-semibold hover:bg-brand-blue-hover transition-colors"
            >
              Start a new workup <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
            </Link>
          )}
        </div>

        {/* Reports list */}
        <div className="mt-10">
          {loading ? (
            <div className="grid gap-4">
              {[1, 2, 3].map((k) => (
                <div key={k} className="h-24 rounded-2xl bg-slate-50 animate-pulse" />
              ))}
            </div>
          ) : reports.length === 0 ? (
            <div
              data-testid="dashboard-empty-state"
              className="rounded-2xl bg-white border border-slate-100 p-10 text-center"
            >
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-blue-light text-brand-blue">
                <Sparkles className="h-6 w-6" strokeWidth={2.2} />
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-ink-900">
                You haven&apos;t generated any workups yet
              </h2>
              <p className="mt-1 text-sm text-ink-600">
                Start one and it will appear here.
              </p>
              <Link
                to="/start"
                data-testid="dashboard-empty-cta"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-blue text-white px-5 py-2.5 text-sm font-semibold hover:bg-brand-blue-hover"
              >
                Start a workup <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
              </Link>
            </div>
          ) : (
            <div data-testid="dashboard-reports-list" className="grid gap-4">
              {reports.map((r) => {
                const s = STATUS_META[r.status] || STATUS_META.locked;
                return (
                  <div
                    key={r.report_id}
                    data-testid={`dashboard-report-${r.report_id}`}
                    className="rounded-2xl bg-white border border-slate-100 p-5 md:p-6 flex flex-wrap items-start justify-between gap-4 hover:shadow-sm transition-shadow"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-bold tracking-widest rounded-full px-2 py-0.5 ${s.className}`}>
                          {r.status === "locked" ? (
                            <span className="inline-flex items-center gap-1">
                              <Lock className="h-3 w-3" strokeWidth={2.4} />
                              {s.label.toUpperCase()}
                            </span>
                          ) : s.label.toUpperCase()}
                        </span>
                        {r.imaging_indicated && (
                          <span className="text-[10px] font-bold tracking-widest rounded-full px-2 py-0.5 bg-amber-50 text-amber-800 border border-amber-200">
                            IMAGING
                          </span>
                        )}
                        <span className="text-xs text-ink-500">
                          {r.created_at?.slice(0, 10)}
                        </span>
                      </div>
                      <p className="mt-2 font-heading text-base font-semibold text-ink-900 leading-snug">
                        {r.symptoms || "Untitled workup"}
                      </p>
                      <p className="mt-1 text-sm text-ink-500 line-clamp-2">
                        {r.summary}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {r.categories.map((c) => (
                          <span key={c} className="text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 bg-slate-50 text-ink-600 border border-slate-100">
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => copyLink(r.report_id)}
                        data-testid={`dashboard-share-${r.report_id}`}
                        className="rounded-full border-slate-200"
                      >
                        <Share2 className="h-4 w-4" strokeWidth={2.2} />
                      </Button>
                      {r.status !== "locked" && (
                        <Button
                          variant="outline"
                          onClick={() => window.open(printReportUrl(r.report_id), "_blank")}
                          data-testid={`dashboard-print-${r.report_id}`}
                          className="rounded-full border-slate-200"
                        >
                          <Printer className="h-4 w-4" strokeWidth={2.2} />
                        </Button>
                      )}
                      <Link
                        to={`/r/${r.report_id}`}
                        data-testid={`dashboard-view-${r.report_id}`}
                        className="inline-flex items-center rounded-full bg-brand-blue text-white px-4 py-2 text-sm font-semibold hover:bg-brand-blue-hover"
                      >
                        View
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
}
